/**
 * Corpus-master microphone capture (ARCHITECTURE.md §9, 红线 §9 22 s).
 *
 * The browser's "voice" processing chain (echo cancellation, noise
 * suppression, AGC) is switched OFF: those algorithms remove exactly the
 * signal a pronunciation corpus is measuring. We capture raw Float32 PCM at
 * the device rate through a ScriptProcessor (with an AudioWorklet fallback), resample to 48 kHz with the shared
 * `resample`, and encode a 16-bit mono WAV with the shared `encodeWav`.
 */
import { MAX_DURATION_MS, TARGET_SAMPLE_RATE, encodeWav, resample } from "@xinghai/shared";

export { MAX_DURATION_MS, TARGET_SAMPLE_RATE };

/** Samples at or above this magnitude count as clipped. */
const CLIP_THRESHOLD = 0.997;
/**
 * Frames quieter than this RMS count as silence.
 *
 * Keep this floor low because browsers expose microphone levels very
 * differently when raw capture is requested (`echoCancellation: false`). On
 * phones and Bluetooth headsets normal speech can sit around 0.004–0.01;
 * 0.012 made those perfectly usable takes look silent and blocked upload.
 */
const SILENCE_RMS = 0.004;
/** Silence analysis window. */
const SILENCE_FRAME_MS = 20;
/** Level callback throttle. */
const LEVEL_INTERVAL_MS = 50;
/**
 * A graph can be constructed successfully while the browser never pulls its
 * input (this is most common after a permission prompt on mobile Safari and
 * some Firefox WebViews).  Do not report "recording" until at least one
 * capture block has arrived.
 */
const STARTUP_TIMEOUT_MS = 1500;

export type RecorderErrorCode =
  "unsupported" | "permission_denied" | "no_device" | "device_busy" | "failed";

export class RecorderError extends Error {
  readonly code: RecorderErrorCode;
  constructor(code: RecorderErrorCode, message: string) {
    super(message);
    this.name = "RecorderError";
    this.code = code;
  }
}

const MESSAGES: Record<RecorderErrorCode, string> = {
  unsupported: "这个浏览器不支持录音，请换用较新的 Chrome、Edge 或 Safari。",
  permission_denied: "没有拿到麦克风权限。请在浏览器地址栏的权限设置里允许使用麦克风，然后重试。",
  no_device: "没有检测到麦克风。请插上耳机或麦克风后重试。",
  device_busy: "麦克风正被其他程序占用。请关闭其他录音软件后重试。",
  failed: "录音没能开始，请重试一次。",
};

export function recorderErrorMessage(code: RecorderErrorCode): string {
  return MESSAGES[code];
}

/** Live input level, emitted roughly every 50 ms while recording. */
export interface RecorderLevel {
  /** 0–1 RMS of the most recent block. */
  rms: number;
  /** 0–1 peak of the most recent block. */
  peak: number;
  /** true once any sample in the take has clipped. */
  clipped: boolean;
}

/** Everything the record page needs after a take. */
export interface RecorderResult {
  /** 48 kHz 16-bit mono WAV. */
  blob: Blob;
  /** Resampled PCM (48 kHz), kept for the waveform preview. */
  pcm: Float32Array;
  sampleRate: number;
  durationMs: number;
  /** Highest absolute sample in the take. */
  peak: number;
  /** RMS over the whole take — the speech level used for the SNR estimate. */
  rms: number;
  /** Number of clipped samples. */
  clipCount: number;
  clipping: boolean;
  /** Fraction of 20 ms frames below the silence threshold, 0–1. */
  silenceRatio: number;
  /** Rate the device actually captured at, before resampling. */
  deviceSampleRate: number;
}

export interface RecorderOptions {
  /** Hard stop, §9. @default 22000 */
  maxMs?: number;
  onLevel?: (level: RecorderLevel) => void;
  onElapsed?: (ms: number) => void;
  /** Fired when the hard limit is reached; the take is already finalised. */
  onAutoStop?: () => void;
}

const WORKLET_SOURCE = `
class XzCaptureProcessor extends AudioWorkletProcessor {
  process(inputs) {
    const ch = inputs[0] && inputs[0][0];
    if (ch && ch.length) {
      const copy = new Float32Array(ch.length);
      copy.set(ch);
      this.port.postMessage(copy, [copy.buffer]);
    }
    return true;
  }
}
registerProcessor('xz-capture', XzCaptureProcessor);
`;

type AnyWindow = typeof globalThis & {
  AudioContext?: typeof AudioContext;
  webkitAudioContext?: typeof AudioContext;
};

function createAudioContext(): AudioContext {
  const w = globalThis as AnyWindow;
  const Ctor = w.AudioContext ?? w.webkitAudioContext;
  if (!Ctor) throw new RecorderError("unsupported", MESSAGES.unsupported);
  return new Ctor();
}

function mapGetUserMediaError(err: unknown): RecorderError {
  const name = (err as { name?: string } | null)?.name ?? "";
  if (name === "NotAllowedError" || name === "SecurityError" || name === "PermissionDeniedError") {
    return new RecorderError("permission_denied", MESSAGES.permission_denied);
  }
  if (
    name === "NotFoundError" ||
    name === "DevicesNotFoundError" ||
    name === "OverconstrainedError"
  ) {
    return new RecorderError("no_device", MESSAGES.no_device);
  }
  if (name === "NotReadableError" || name === "TrackStartError") {
    return new RecorderError("device_busy", MESSAGES.device_busy);
  }
  return new RecorderError("failed", MESSAGES.failed);
}

/**
 * Ask for the least processed mono signal first, then retry with the browser's
 * plain `audio: true` constraint when an older engine rejects one of the
 * optional constraints.  `channelCount: 1` as an exact number is needlessly
 * strict on a few Firefox/mobile devices; an ideal value still lets the Web
 * Audio graph down-mix to one channel.
 */
async function requestMicrophone(md: MediaDevices): Promise<MediaStream> {
  try {
    return await md.getUserMedia({
      audio: {
        channelCount: { ideal: 1 },
        // corpus master: keep the raw signal, §9.  These are preferences;
        // browsers that do not expose them simply ignore them.
        echoCancellation: false,
        noiseSuppression: false,
        autoGainControl: false,
      },
      video: false,
    });
  } catch (err) {
    const name = (err as { name?: string } | null)?.name ?? "";
    // Overconstrained/TypeError means this particular constraint shape was
    // rejected.  A second request with the baseline boolean is broadly
    // supported and does not bypass permission or device errors.
    if (name !== "OverconstrainedError" && name !== "TypeError") throw err;
    return md.getUserMedia({ audio: true, video: false });
  }
}

/** Root-mean-square level of a buffer, 0–1. */
export function rmsOf(pcm: Float32Array): number {
  if (pcm.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < pcm.length; i += 1) {
    const v = pcm[i] ?? 0;
    sum += v * v;
  }
  return Math.sqrt(sum / pcm.length);
}

/** Fraction of 20 ms frames whose RMS is below the silence floor. */
export function silenceRatio(pcm: Float32Array, sampleRate: number): number {
  const frame = Math.max(1, Math.round((SILENCE_FRAME_MS / 1000) * sampleRate));
  const frames = Math.floor(pcm.length / frame);
  if (frames === 0) return pcm.length === 0 ? 1 : 0;
  let silent = 0;
  for (let f = 0; f < frames; f += 1) {
    let sum = 0;
    const start = f * frame;
    for (let i = start; i < start + frame; i += 1) {
      const v = pcm[i] ?? 0;
      sum += v * v;
    }
    if (Math.sqrt(sum / frame) < SILENCE_RMS) silent += 1;
  }
  return silent / frames;
}

/** Concatenate the captured blocks into one buffer. */
function concat(blocks: Float32Array[], total: number): Float32Array {
  const out = new Float32Array(total);
  let off = 0;
  for (const b of blocks) {
    out.set(b, off);
    off += b.length;
  }
  return out;
}

export class Recorder {
  private readonly maxMs: number;
  private readonly opts: RecorderOptions;

  private stream: MediaStream | null = null;
  private ctx: AudioContext | null = null;
  private source: MediaStreamAudioSourceNode | null = null;
  private node: AudioNode | null = null;
  private workletUrl: string | null = null;

  private blocks: Float32Array[] = [];
  private samples = 0;
  private deviceRate = TARGET_SAMPLE_RATE;
  private clipCount = 0;
  private peak = 0;
  private lastLevelAt = 0;
  private startedAt = 0;
  private stoppedAt = 0;
  private running = false;
  private autoStopped = false;
  private timer: ReturnType<typeof setInterval> | null = null;
  private firstBlock: Promise<void> | null = null;
  private resolveFirstBlock: (() => void) | null = null;
  private rejectFirstBlock: ((reason?: unknown) => void) | null = null;
  private firstBlockSeen = false;

  constructor(opts: RecorderOptions = {}) {
    this.opts = opts;
    this.maxMs = opts.maxMs ?? MAX_DURATION_MS;
  }

  get isRecording(): boolean {
    return this.running;
  }

  get elapsedMs(): number {
    if (!this.running && this.stoppedAt === 0) return 0;
    const end = this.running ? performance.now() : this.stoppedAt;
    return Math.max(0, end - this.startedAt);
  }

  /** Request the mic and begin capturing. Throws `RecorderError`. */
  async start(): Promise<void> {
    if (this.running) return;
    const md = navigator.mediaDevices;
    if (!md?.getUserMedia) throw new RecorderError("unsupported", MESSAGES.unsupported);

    let stream: MediaStream;
    let ctx: AudioContext;
    try {
      /*
       * Construct/resume the context in the original button activation.  A
       * permission prompt is asynchronous; waiting for getUserMedia first
       * can lose the transient user activation required by Safari/iOS.
       * We deliberately do not await this first resume until after the mic
       * request has been started.
       */
      ctx = createAudioContext();
      this.ctx = ctx;
      const resumeBeforePermission =
        ctx.state === "suspended" ? ctx.resume().catch(() => undefined) : Promise.resolve();
      const streamPromise = requestMicrophone(md);
      stream = await streamPromise;
      await resumeBeforePermission;
    } catch (err) {
      this.teardown();
      if (err instanceof RecorderError) throw err;
      throw mapGetUserMediaError(err);
    }

    this.stream = stream;
    this.blocks = [];
    this.samples = 0;
    this.clipCount = 0;
    this.peak = 0;
    this.autoStopped = false;
    this.stoppedAt = 0;

    try {
      // A second resume is useful on Firefox/Safari after a permission prompt
      // and is harmless when the context already became running.
      if (ctx.state === "suspended") await ctx.resume();
      this.deviceRate = ctx.sampleRate || TARGET_SAMPLE_RATE;
      this.source = ctx.createMediaStreamSource(stream);
      this.node = await this.createCaptureNode(ctx);

      this.firstBlockSeen = false;
      this.firstBlock = new Promise<void>((resolve, reject) => {
        this.resolveFirstBlock = resolve;
        this.rejectFirstBlock = reject;
      });
      this.running = true;
      this.startedAt = performance.now();
      this.lastLevelAt = 0;

      this.source.connect(this.node);
      // A muted sink keeps the graph pulling without monitoring the mic.
      const sink = ctx.createGain();
      sink.gain.value = 0;
      this.node.connect(sink);
      sink.connect(ctx.destination);

      // A successful getUserMedia call is not enough: if the graph never
      // produces a block, the eventual WAV is empty and the UI reports that
      // the voice was not detected.  Fail early with a retryable error.
      const firstBlock = this.firstBlock;
      if (firstBlock) {
        let timeout: ReturnType<typeof setTimeout> | null = null;
        const timedOut = new Promise<false>((resolve) => {
          timeout = setTimeout(() => resolve(false), STARTUP_TIMEOUT_MS);
        });
        const received = await Promise.race([firstBlock.then(() => true), timedOut]);
        if (timeout !== null) clearTimeout(timeout);
        this.firstBlock = null;
        this.resolveFirstBlock = null;
        this.rejectFirstBlock = null;
        if (!received) {
          throw new RecorderError("failed", MESSAGES.failed);
        }
      }
    } catch (err) {
      this.running = false;
      this.teardown();
      if (err instanceof RecorderError) throw err;
      throw new RecorderError("failed", MESSAGES.failed);
    }

    this.timer = setInterval(() => this.opts.onElapsed?.(this.elapsedMs), 100);
    this.opts.onElapsed?.(0);
  }

  private async createCaptureNode(ctx: AudioContext): Promise<AudioNode> {
    const onBlock = (block: Float32Array) => {
      if (block.length > 0 && !this.firstBlockSeen) {
        this.firstBlockSeen = true;
        this.resolveFirstBlock?.();
      }
      this.consume(block);
    };

    // ScriptProcessor is deprecated, but it remains the most dependable path
    // on Firefox for Android/iOS WebViews. Some Firefox builds successfully
    // load a Blob AudioWorklet yet never pull its input node, resulting in an
    // apparently working recorder whose WAV contains only silence. Keep the
    // small 4096-frame callback as the primary path for broad compatibility.
    if (typeof ctx.createScriptProcessor === "function") {
      const proc = ctx.createScriptProcessor(4096, 1, 1);
      proc.onaudioprocess = (e: AudioProcessingEvent) => {
        const ch = e.inputBuffer.getChannelData(0);
        const copy = new Float32Array(ch.length);
        copy.set(ch);
        onBlock(copy);
      };
      return proc;
    }

    if (ctx.audioWorklet?.addModule) {
      try {
        const url = URL.createObjectURL(
          new Blob([WORKLET_SOURCE], { type: "application/javascript" }),
        );
        this.workletUrl = url;
        await ctx.audioWorklet.addModule(url);
        const node = new AudioWorkletNode(ctx, "xz-capture", {
          numberOfInputs: 1,
          numberOfOutputs: 1,
          channelCount: 1,
        });
        node.port.onmessage = (e: MessageEvent<Float32Array>) => onBlock(e.data);
        return node;
      } catch {
        // fall through to ScriptProcessor
      }
    }
    throw new RecorderError("unsupported", MESSAGES.unsupported);
  }

  private consume(block: Float32Array): void {
    if (!this.running) return;
    const limitSamples = Math.floor((this.maxMs / 1000) * this.deviceRate);
    let use = block;
    if (this.samples + block.length >= limitSamples) {
      use = block.subarray(0, Math.max(0, limitSamples - this.samples));
    }
    let sum = 0;
    let blockPeak = 0;
    for (let i = 0; i < use.length; i += 1) {
      const v = use[i] ?? 0;
      const a = Math.abs(v);
      sum += v * v;
      if (a > blockPeak) blockPeak = a;
      if (a >= CLIP_THRESHOLD) this.clipCount += 1;
    }
    if (blockPeak > this.peak) this.peak = blockPeak;
    if (use.length > 0) {
      this.blocks.push(use);
      this.samples += use.length;
    }

    const now = performance.now();
    if (now - this.lastLevelAt >= LEVEL_INTERVAL_MS && use.length > 0) {
      this.lastLevelAt = now;
      this.opts.onLevel?.({
        rms: Math.sqrt(sum / use.length),
        peak: blockPeak,
        clipped: this.clipCount > 0,
      });
    }

    if (this.samples >= limitSamples && !this.autoStopped) {
      this.autoStopped = true;
      this.running = false;
      this.stoppedAt = performance.now();
      this.opts.onElapsed?.(this.maxMs);
      this.opts.onAutoStop?.();
    }
  }

  /** Finalise the take: resample to 48 kHz, encode WAV, release the mic. */
  async stop(): Promise<RecorderResult> {
    if (this.stoppedAt === 0) this.stoppedAt = performance.now();
    this.running = false;
    const deviceRate = this.deviceRate;
    const raw = concat(this.blocks, this.samples);
    const clipCount = this.clipCount;
    const peak = this.peak;
    this.teardown();

    const pcm = resample(raw, deviceRate, TARGET_SAMPLE_RATE);
    const buffer = encodeWav([pcm], TARGET_SAMPLE_RATE);
    const durationMs = Math.round((pcm.length / TARGET_SAMPLE_RATE) * 1000);
    return {
      blob: new Blob([buffer], { type: "audio/wav" }),
      pcm,
      sampleRate: TARGET_SAMPLE_RATE,
      durationMs,
      peak,
      rms: rmsOf(pcm),
      clipCount,
      clipping: clipCount > 0,
      silenceRatio: silenceRatio(pcm, TARGET_SAMPLE_RATE),
      deviceSampleRate: deviceRate,
    };
  }

  /** Abandon the take and release the mic. */
  cancel(): void {
    this.running = false;
    this.stoppedAt = performance.now();
    this.blocks = [];
    this.samples = 0;
    this.teardown();
  }

  private teardown(): void {
    if (this.timer !== null) {
      clearInterval(this.timer);
      this.timer = null;
    }
    try {
      if (this.node && "port" in this.node) {
        (this.node as AudioWorkletNode).port.onmessage = null;
      }
      if (this.node && "onaudioprocess" in this.node) {
        (this.node as ScriptProcessorNode).onaudioprocess = null;
      }
      this.source?.disconnect();
      this.node?.disconnect();
    } catch {
      /* ignore */
    }
    this.source = null;
    this.node = null;
    // If start() is still waiting for the first callback (for example because
    // the page was unmounted), reject that wait so useRecorder can return to
    // idle instead of later switching back to "recording" after cancellation.
    this.rejectFirstBlock?.(new RecorderError("failed", MESSAGES.failed));
    this.resolveFirstBlock = null;
    this.rejectFirstBlock = null;
    this.firstBlock = null;
    for (const track of this.stream?.getTracks() ?? []) track.stop();
    this.stream = null;
    void this.ctx?.close?.().catch(() => undefined);
    this.ctx = null;
    if (this.workletUrl) {
      URL.revokeObjectURL(this.workletUrl);
      this.workletUrl = null;
    }
  }
}
